import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Enhanced SEO Component for comprehensive meta tag management
 * Supports Open Graph, Twitter Cards, canonical URLs, and UK-focused SEO
 */

const SITE_URL = 'https://www.furd.co.uk';
const SITE_NAME = 'FURD Ltd';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_furd-mep/artifacts/93z01v59_FURD.jpg';
const DEFAULT_DESCRIPTION = 'Leading UK MEP Engineering & Building Services Consultancy. Expert mechanical, electrical, and public health design for commercial, residential, healthcare, and education projects in London and across the UK.';

const SEO = ({ 
  // Basic SEO
  title,
  description,
  keywords,
  
  // URLs
  url,
  canonicalUrl,
  
  // Images
  image,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  
  // Content type
  type = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
  
  // Indexing control
  noIndex = false,
  noFollow = false,
  
  // Additional
  author,
  locale = 'en_GB',
  alternateLocales = [],
  
  // Children for additional meta tags
  children
}) => {
  // Build full title
  const fullTitle = title 
    ? `${title} | ${SITE_NAME} - MEP Engineering Consultants` 
    : `${SITE_NAME} - MEP Engineering & Building Services Consultancy UK`;
  
  // Determine URLs
  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : SITE_URL);
  const canonical = canonicalUrl || pageUrl;
  
  // Build robots directive
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow',
    'max-image-preview:large',
    'max-snippet:-1',
    'max-video-preview:-1'
  ].join(', ');

  // Build keywords string - always include core MEP terms
  const coreKeywords = [
    'MEP engineering',
    'building services consultancy',
    'mechanical engineering London',
    'electrical engineering UK',
    'public health design'
  ];
  
  const allKeywords = keywords 
    ? [...new Set([...keywords.split(',').map(k => k.trim()), ...coreKeywords])].join(', ')
    : coreKeywords.join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="keywords" content={allKeywords} />
      
      {/* Robots & Indexing */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content={robotsContent} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Language & Region */}
      <meta name="language" content="English" />
      <meta httpEquiv="content-language" content="en-GB" />
      <html lang="en-GB" />
      
      {/* Author & Publisher */}
      <meta name="author" content={author || SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="copyright" content={`© ${new Date().getFullYear()} ${SITE_NAME}`} />
      
      {/* Geographic targeting for UK SEO */}
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="London, United Kingdom" />
      <meta name="geo.position" content="51.513458;-0.098946" />
      <meta name="ICBM" content="51.513458, -0.098946" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={image || DEFAULT_IMAGE} />
      <meta property="og:image:alt" content={imageAlt || title || 'FURD Ltd MEP Engineering'} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      {alternateLocales.map(loc => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}
      
      {/* Article specific Open Graph */}
      {type === 'article' && (
        <>
          {articlePublishedTime && (
            <meta property="article:published_time" content={articlePublishedTime} />
          )}
          {articleModifiedTime && (
            <meta property="article:modified_time" content={articleModifiedTime} />
          )}
          {articleSection && (
            <meta property="article:section" content={articleSection} />
          )}
          {articleTags && articleTags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
          <meta property="article:publisher" content={SITE_URL} />
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={image || DEFAULT_IMAGE} />
      <meta name="twitter:image:alt" content={imageAlt || title || 'FURD Ltd MEP Engineering'} />
      
      {/* Mobile & App */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      
      {/* Theme */}
      <meta name="theme-color" content="#1e3a5f" />
      <meta name="msapplication-TileColor" content="#1e3a5f" />
      
      {/* Verification - Add your verification codes here */}
      {/* <meta name="google-site-verification" content="YOUR_CODE" /> */}
      {/* <meta name="msvalidate.01" content="YOUR_CODE" /> */}
      
      {/* Additional children meta tags */}
      {children}
    </Helmet>
  );
};

// Pre-configured SEO for specific page types
export const HomePageSEO = (props) => (
  <SEO
    title="Leading MEP Engineering & Building Services Consultancy"
    description="FURD Ltd is a leading UK MEP engineering consultancy providing mechanical, electrical, and public health design services for commercial, residential, healthcare, and education projects in London and across the UK."
    keywords="MEP engineering UK, building services consultancy London, mechanical engineering consultants, electrical design consultants, public health engineering, HVAC design UK, sustainability consultants, net zero building design"
    {...props}
  />
);

export const ServicesPageSEO = (props) => (
  <SEO
    title="MEP Engineering & Building Services"
    description="Comprehensive MEP engineering services including mechanical design, electrical engineering, public health, sustainability consultancy, and building physics. Expert building services for UK projects."
    keywords="MEP services UK, mechanical engineering services, electrical engineering services, public health design, HVAC consultancy, sustainability engineering, Part L compliance, BREEAM assessment"
    {...props}
  />
);

export const ProjectsPageSEO = (props) => (
  <SEO
    title="MEP Engineering Projects Portfolio"
    description="Explore our portfolio of MEP engineering and building services projects across commercial, residential, healthcare, and education sectors throughout the UK."
    keywords="MEP projects UK, building services portfolio, engineering case studies, commercial projects London, residential MEP, healthcare engineering projects"
    {...props}
  />
);

export const TeamPageSEO = (props) => (
  <SEO
    title="Meet Our MEP Engineering Team"
    description="Meet the expert engineers and consultants at FURD Ltd. Our team brings decades of experience in MEP design, sustainability, and building services engineering."
    keywords="MEP engineers UK, building services consultants, mechanical engineers London, electrical engineers, sustainability consultants team"
    {...props}
  />
);

export const AboutPageSEO = (props) => (
  <SEO
    title="About FURD - MEP Engineering Consultancy"
    description="Learn about FURD Ltd, a leading UK MEP engineering consultancy. Discover our values, approach, and commitment to excellence in building services design."
    keywords="about FURD, MEP consultancy UK, building services company, engineering firm London, MEP design consultants"
    {...props}
  />
);

export const ContactPageSEO = (props) => (
  <SEO
    title="Contact Us - MEP Engineering Enquiries"
    description="Get in touch with FURD Ltd for MEP engineering and building services consultancy. Contact our London office for project enquiries and expert advice."
    keywords="contact MEP engineers, building services enquiry, MEP consultancy London, engineering quote, project enquiry"
    {...props}
  />
);

export const CareersPageSEO = (props) => (
  <SEO
    title="Careers in MEP Engineering"
    description="Join FURD Ltd and build your career in MEP engineering. Explore opportunities in mechanical, electrical, and sustainability engineering across the UK."
    keywords="MEP engineering jobs, building services careers, mechanical engineer vacancies, electrical engineer jobs London, sustainability consultant positions"
    {...props}
  />
);

export const NewsPageSEO = (props) => (
  <SEO
    title="Industry Insights & News"
    description="Stay informed with the latest news, insights, and updates from FURD Ltd. Expert commentary on MEP engineering, building regulations, and sustainability trends."
    keywords="MEP engineering news, building services insights, construction industry updates, Part L news, net zero updates, sustainability trends"
    type="website"
    {...props}
  />
);

export default SEO;
